import { useState, useEffect, useCallback } from 'react';
import { authAPI, formatApiError } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  KeyRound, Loader2, Save, AlertTriangle, CheckCircle2, Server, Database, Eye, EyeOff,
} from 'lucide-react';
import { toast } from 'sonner';

/**
 * El registro en Entra ID que hace andar el botón "Entrar con Microsoft".
 *
 * **Por qué está en esta pantalla y no en otra.** Era la última credencial del sistema que no
 * se podía ver ni cambiar desde la aplicación: vivía en el `.env` del servidor, fuera de toda
 * pantalla y de todo auditor. Rotarla exigía editar un archivo y reconstruir la imagen.
 *
 * **Y por qué está en una solapa aparte de las conexiones.** Una conexión es del cliente y
 * de una cápsula; esto es de la plataforma entera y no tiene tenant. Mezclarlas en la misma
 * lista haría creer que se puede tener un SSO por cápsula, que es justo lo que no se puede.
 */
export default function IngresoPorMicrosoft() {
  const [conf, setConf] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [verSecreto, setVerSecreto] = useState(false);
  // El secreto llega enmascarado. Mientras nadie lo toque se manda de vuelta tal cual y el
  // backend lo reconoce por el `***` y no lo pisa; esta bandera es solo para el cartel.
  const [secretoTocado, setSecretoTocado] = useState(false);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const { data } = await authAPI.oidcConfiguracion();
      setConf(data);
      setSecretoTocado(false);
    } catch (err) {
      toast.error(formatApiError(err) || 'No se pudo leer la configuración del ingreso');
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const guardar = async () => {
    setGuardando(true);
    try {
      const { data } = await authAPI.guardarOidcConfiguracion({
        client_id: conf.client_id || '',
        client_secret: conf.client_secret || '',
        authority: conf.authority || '',
        redirect_uri: conf.redirect_uri || '',
      });
      setConf(data);
      setSecretoTocado(false);
      toast.success('Guardado. Se aplica sin reiniciar: probá el botón de Microsoft.');
    } catch (err) {
      toast.error(formatApiError(err) || 'No se pudo guardar');
    } finally {
      setGuardando(false);
    }
  };

  const set = (k) => (e) => setConf((c) => ({ ...c, [k]: e.target.value }));

  if (cargando) {
    return (
      <div className="flex items-center gap-2 text-xs text-zinc-500 p-4">
        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Cargando…
      </div>
    );
  }
  if (!conf) return null;

  const deLaBase = conf.origen === 'base';

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <Card className="rounded-sm border-brand-border">
        <CardHeader className="py-2 px-3 border-b border-brand-border flex flex-row items-center justify-between">
          <CardTitle className="text-[10px] font-semibold uppercase tracking-[0.1em] text-zinc-600">
            Aplicación de Entra ID
          </CardTitle>
          {conf.configurado ? (
            <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px] gap-1">
              <CheckCircle2 className="w-3 h-3" /> Configurada
            </Badge>
          ) : (
            <Badge className="bg-amber-50 text-amber-700 border-amber-200 text-[10px]">
              Sin configurar
            </Badge>
          )}
        </CardHeader>

        <CardContent className="p-3 space-y-3">
          {/* De donde sale hoy. Importa tanto como los valores: mientras diga "entorno", lo
              que se escriba aca no es lo que esta rigiendo. */}
          <div className={`flex items-start gap-2 rounded-sm border p-2 text-[11px] ${
            deLaBase ? 'border-emerald-200 bg-emerald-50/60 text-emerald-800'
                     : 'border-zinc-200 bg-zinc-50 text-zinc-600'}`}>
            {deLaBase ? <Database className="w-3.5 h-3.5 mt-px shrink-0" />
                      : <Server className="w-3.5 h-3.5 mt-px shrink-0" />}
            <span>
              {deLaBase ? (
                <>Sale de <strong>esta pantalla</strong>. Los cambios se aplican sin reiniciar.</>
              ) : (
                <>
                  Sale de las <strong>variables de entorno del servidor</strong>, que es lo que
                  había antes. Lo que ves acá es eso, de solo lectura en la práctica: hasta que
                  guardes, cambiar el SSO sigue exigiendo un redeploy.
                </>
              )}
            </span>
          </div>

          <div className="space-y-1">
            <Label className="text-[11px] text-zinc-600">Client ID (aplicación)</Label>
            <Input value={conf.client_id || ''} onChange={set('client_id')}
                   data-testid="sso-client-id"
                   className="h-8 text-xs rounded-sm font-mono" placeholder="00000000-0000-…" />
          </div>

          <div className="space-y-1">
            <Label className="text-[11px] text-zinc-600">Client secret</Label>
            <div className="flex gap-1">
              <Input type={verSecreto ? 'text' : 'password'}
                     value={conf.client_secret || ''}
                     data-testid="sso-client-secret"
                     onChange={(e) => { setSecretoTocado(true); set('client_secret')(e); }}
                     className="h-8 text-xs rounded-sm font-mono" placeholder="Sin configurar" />
              <Button type="button" variant="outline" size="sm"
                      className="h-8 px-2 rounded-sm"
                      onClick={() => setVerSecreto((v) => !v)}>
                {verSecreto ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
              </Button>
            </div>
            <p className="text-[10px] text-zinc-500">
              {secretoTocado
                ? 'Se va a reemplazar el secreto guardado.'
                : 'Llega enmascarado. Dejalo como está y no se toca; escribí encima solo para cambiarlo.'}
            </p>
          </div>

          <div className="space-y-1">
            <Label className="text-[11px] text-zinc-600">Authority</Label>
            <Input value={conf.authority || ''} onChange={set('authority')}
                   className="h-8 text-xs rounded-sm font-mono" />
          </div>

          <div className="space-y-1">
            <Label className="text-[11px] text-zinc-600">Redirect URI</Label>
            <Input value={conf.redirect_uri || ''} onChange={set('redirect_uri')}
                   className="h-8 text-xs rounded-sm font-mono"
                   placeholder="Se deduce del pedido si se deja vacío" />
            <p className="text-[10px] text-zinc-500">
              Tiene que coincidir exactamente con la que figura en el registro de Entra.
            </p>
          </div>

          <Button onClick={guardar} disabled={guardando || !conf.client_id}
                  data-testid="sso-guardar"
                  className="h-8 text-xs rounded-sm gap-1.5">
            {guardando ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                       : <Save className="w-3.5 h-3.5" />}
            Guardar
          </Button>
        </CardContent>
      </Card>

      <Card className="rounded-sm border-brand-border">
        <CardHeader className="py-2 px-3 border-b border-brand-border">
          <CardTitle className="text-[10px] font-semibold uppercase tracking-[0.1em] text-zinc-600">
            Antes de tocar esto
          </CardTitle>
        </CardHeader>
        <CardContent className="p-3 space-y-3 text-[11px] text-zinc-600 leading-relaxed">
          <div className="flex items-start gap-2 rounded-sm border border-amber-200 bg-amber-50/60 p-2 text-amber-900">
            <AlertTriangle className="w-3.5 h-3.5 mt-px shrink-0" />
            <span>
              Esto es el <strong>botón de ingreso</strong>. Si queda mal, nadie entra con
              Microsoft — pero el ingreso <strong>con contraseña sigue andando</strong> y no
              depende de nada de acá. Ésa es la red: se entra con contraseña y se corrige.
            </span>
          </div>

          <p>
            <strong>No es la conexión con Microsoft 365.</strong> Ésta es la aplicación de la
            plataforma y solo sirve para averiguar quién está entrando. La que lee correo, Teams
            y el directorio es la conexión <code className="text-[10px]">microsoft-365</code>, de
            la solapa de al lado, y es del cliente. Son dos registros distintos.
          </p>

          <p>
            <strong>Es una sola para todo el sistema</strong>, no una por cápsula ni por cliente:
            cuando alguien aprieta el botón todavía no se sabe de qué tenant es. Quién puede
            entrar se decide después, con los dominios permitidos de cada tenant.
          </p>

          <p>
            <strong>No se valida al guardar.</strong> Probar esta credencial es hacer el ingreso
            entero, con un navegador y una cuenta real — no hay forma de verificarla desde acá.
            Después de guardar, salí y entrá con el botón de Microsoft.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
